// src/components/messaging/ConversationList.tsx
import React, { useState } from 'react';
import { Conversation } from '../../types/message';
import ConversationItem from './ConversationItem';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  onConversationSelect: (conversationId: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversation,
  onConversationSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredConversations = conversations.filter(conversation =>
    conversation.participant.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conversation.participant.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conversation.lastMessage?.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatLastMessageTime = (timestamp: string) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b-2 border-coffee">
        <h2 className="text-coffee font-bold text-lg mb-4">Conversations</h2>
        
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white border-2 border-coffee text-coffee placeholder-coffee/50 rounded-lg px-4 py-2 focus:outline-none focus:ring-terracotta focus:border-terracotta"
          />
          <div className="absolute right-3 top-2.5 text-coffee/60">
            🔍
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length > 0 ? (
          <div className="space-y-1">
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isActive={activeConversation?.id === conversation.id}
                onClick={() => onConversationSelect(conversation.id)}
                lastMessageTime={formatLastMessageTime(conversation.updatedAt)}
              />
            ))}
          </div>
        ) : (
          <div className="p-4 text-center">
            <div className="text-coffee/70 text-sm">
              {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t-2 border-coffee">
        <div className="text-coffee/70 text-xs text-center">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

export default ConversationList;